import React, { useRef, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { TherapistProfile } from "@/types/therapist";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Mail, Globe, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface TherapistCardProps {
  therapist: TherapistProfile;
  userRole: string | null;
}

const TherapistCard = ({ therapist, userRole }: TherapistCardProps) => {
  const [showContactDialog, setShowContactDialog] = React.useState(false);
  const [showLoginDialog, setShowLoginDialog] = React.useState(false);
  const [showTherapistDialog, setShowTherapistDialog] = React.useState(false);
  const navigate = useNavigate();
  
  const getInitials = (firstName: string | null, lastName: string | null) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (lastName) {
      return lastName.substring(0, 2).toUpperCase();
    }
    return "TH";
  };

  const handleRequestConnection = async (therapistId: string) => {
    if (!userRole) {
      // If user is not logged in, show login prompt
      toast.info("Please log in to connect with therapists");
      navigate('/auth');
      return;
    }
    // Keep the dialog visible instead of making the connection
    setShowContactDialog(true);
  };

  const handleViewContactInfo = () => {
    if (!userRole) {
      toast.info("Please log in to view contact information");
      navigate('/auth');
      return;
    }
  };

  const handleEmailClick = (e: React.MouseEvent, email: string) => {
    e.stopPropagation();
    if (userRole) {
      window.location.href = `mailto:${email}`;
    } else {
      setShowTherapistDialog(true);
      setShowLoginDialog(false);
    }
  };

  const handleWebsiteClick = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    if (userRole) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setShowTherapistDialog(true);
      setShowLoginDialog(false);
    }
  };

  const handleBookSession = () => {
    if (!userRole) {
      setShowLoginDialog(true);
      setShowTherapistDialog(false);
    } else {
      handleRequestConnection(therapist.id);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (showTherapistDialog || showLoginDialog) return;
    if (userRole) return;
    setShowTherapistDialog(true);
    setShowLoginDialog(false);
  };

  // Add state and ref for bio truncation
  const bioRef = useRef<HTMLParagraphElement>(null);
  const [isBioTruncated, setIsBioTruncated] = useState(false);

  useEffect(() => {
    if (bioRef.current) {
      setIsBioTruncated(bioRef.current.scrollHeight > bioRef.current.clientHeight || bioRef.current.scrollWidth > bioRef.current.clientWidth);
    }
  }, [therapist.profile_bio]);

  return (
    <Card 
      key={therapist.id} 
      className={`hover:shadow-md transition-shadow min-h-[340px] ${!userRole ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center space-x-4">
          <Avatar className="h-12 w-12 border-2 border-[#CCCCCC]">
            <AvatarImage src={therapist.profile_image_url || undefined} />
            <AvatarFallback className="bg-[#3BAFDA] text-white">
              {getInitials(therapist.first_name, therapist.last_name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <CardTitle className="text-lg">
              {therapist.first_name} {therapist.last_name}
            </CardTitle>
            {therapist.company && (
              <CardDescription>{therapist.company}</CardDescription>
            )}
          </div>
        </div>
        <hr className="w-3/4 mx-auto border-t border-[#CCCCCC] my-2" />
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex flex-wrap text-sm mb-2">
          {therapist.country && (
            <div className="flex items-center mr-2 mb-2">
              <span className="font-medium mr-1">Country:</span>
              <span className="bg-[#E8F0F8] text-[#333B44] rounded-full px-3 py-1 text-xs">
                {therapist.country}
              </span>
            </div>
          )}
          {(therapist.language || therapist.language_secondary) && (
            <div className="flex flex-wrap items-center mb-2">
              <span className="font-medium mr-1">Languages:</span>
              {therapist.language && (
                <span className="bg-[#E8F0F8] text-[#333B44] rounded-full px-3 py-1 text-xs mr-2">
                  {therapist.language}
                </span>
              )}
              {therapist.language_secondary && (
                <span className="bg-[#E8F0F8] text-[#333B44] rounded-full px-3 py-1 text-xs">
                  {therapist.language_secondary}
                </span>
              )}
            </div>
          )}
        </div>
        {therapist.profile_bio && (
          isBioTruncated ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p ref={bioRef} className="text-sm text-muted-foreground line-clamp-8 cursor-help whitespace-pre-line border border-[#CCCCCC] rounded bg-white shadow-sm p-2">
                    {therapist.profile_bio}
                  </p>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm bg-white backdrop-blur-sm border border-[#CCCCCC] p-3" style={{ whiteSpace: 'pre-line' }}>
                  <p className="text-sm" style={{ whiteSpace: 'pre-line' }}>{therapist.profile_bio}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <p ref={bioRef} className="text-sm text-muted-foreground line-clamp-8 whitespace-pre-line border border-[#CCCCCC] rounded bg-white shadow-sm p-2">
              {therapist.profile_bio}
            </p>
          )
        )}
        
        {/* Contact information section */}
        <div className="mt-4 space-y-2">
          {therapist.email && (
            <div 
              className="flex items-center space-x-2 mb-1.5 cursor-pointer group relative w-3/4 mx-auto"
              onClick={(e) => handleEmailClick(e, therapist.email!)}
            >
              <Mail className="h-4 w-4 text-[#5A636E]" />
              <div className="relative w-full">
                <div className={`text-sm text-[#333B44] overflow-hidden text-ellipsis hover:text-[#333B44] transition-colors ${!userRole ? 'blur-sm' : ''}`}>
                  {therapist.email}
                </div>
                {!userRole && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white backdrop-blur-[2px] rounded">
                    <Lock className="h-4 w-4 text-[#3BAFDA]" />
                  </div>
                )}
              </div>
            </div>
          )}
          
          {therapist.website_url && (
            <div 
              className="flex items-center space-x-2 cursor-pointer group relative w-3/4 mx-auto mb-2"
              onClick={(e) => handleWebsiteClick(e, therapist.website_url!)}
            >
              <Globe className="h-4 w-4 text-[#5A636E]" />
              <div className="relative w-full">
                <div className={`text-sm text-[#333B44] overflow-hidden text-ellipsis hover:text-[#333B44] transition-colors ${!userRole ? 'blur-sm' : ''}`}>
                  {therapist.website_url.replace(/(^\w+:|^)\/\//, '')}
                </div>
                {!userRole && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white backdrop-blur-[2px] rounded">
                    <Lock className="h-4 w-4 text-[#3BAFDA]" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter>
        {userRole === 'client' || userRole === 'therapist' ? (
          <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
            <DialogTrigger asChild>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full flex justify-center">
                      <Button 
                        onClick={() => handleRequestConnection(therapist.id)}
                        className="px-4 py-1 text-xs sm:px-6 sm:py-2 sm:text-base bg-[#3BAFDA] hover:bg-[#2a9fc4] text-white"
                      >
                        Book a Session
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-white backdrop-blur-sm border border-[#CCCCCC] p-3">
                    <p className="text-sm">Connect directly to this Therapist</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                {therapist.booking_calendar ? (
                  <>
                    <DialogTitle>Book a Session</DialogTitle>
                    <DialogDescription>
                      You will be redirected to this therapist's booking calendar.{' '}
                      <a
                        href={therapist.booking_calendar}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#333B44] hover:text-[#333B44] font-medium underline"
                      >
                        Open Booking Calendar
                      </a>
                    </DialogDescription>
                  </>
                ) : (
                  <>
                    <DialogTitle>Book a Session</DialogTitle>
                    <DialogDescription>
                      This therapist has not yet set up their booking calendar. Contact them directly.
                    </DialogDescription>
                  </>
                )}
              </DialogHeader>
            </DialogContent>
          </Dialog>
        ) : (
          <>
            <div className="w-full flex justify-center">
              <Button
                className="px-4 py-1 text-xs sm:px-6 sm:py-2 sm:text-base bg-[#3BAFDA] hover:bg-[#2a9fc4] text-white cursor-pointer"
                onClick={handleBookSession}
                type="button"
              >
                <span className="flex items-center justify-center w-full">
                  Book a Session
                </span>
              </Button>
            </div>

            <Dialog 
              open={showLoginDialog} 
              onOpenChange={(open) => {
                setShowLoginDialog(open);
              }}
            >
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Book a Session</DialogTitle>
                  <DialogDescription>
                    To book a session with {therapist.first_name} {therapist.last_name}, you need to be logged in.
                    <div className="mt-4">
                      <a
                        href="/auth"
                        className="text-[#333B44] hover:text-[#333B44] font-medium underline"
                      >
                        Log in to continue
                      </a>
                    </div>
                  </DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
          </>
        )}
      </CardFooter>

      <Dialog open={showTherapistDialog} onOpenChange={setShowTherapistDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>View Therapist Profile</DialogTitle>
            <DialogDescription>
              To view {therapist.first_name} {therapist.last_name}'s contact information and book a session, you need to be logged in.
              <div className="mt-4">
                <a
                  href="/auth"
                  className="text-[#333B44] hover:text-[#333B44] font-medium underline"
                >
                  Log in to continue
                </a>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TherapistCard;
